import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["condition", "error", "hideExtraInputs", "inputUse", "onBlur", "onChange", "getIsUngated", "getTimezoneOptions", "useOnePanel"];
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import I18n from 'I18n';
import { getTransferableProps } from './filterInputProps';
import { fromSimpleDateToMoment, fromMomentToSimpleDate } from 'customer-data-filters-ui/utils/momentSimpleDateConversion';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { DateRange } from 'foundations-components/transitional/components/DateRange';
import { useEffect, useRef, useState } from 'react';
import TimezoneToggle from './TimezoneToggle';
import UpdateDateFilterWithTimezonesNotification from './UpdateDateFilterWithTimezonesNotification';
import { DATE_TIME } from 'customer-data-objects/property/PropertyTypes';
import { getIsUTC as getIsUTCFromUtils } from '../../utils/timezoneUtils';
import { hasRemoveTimezoneForDates } from '../../permissions/gates';
import BetweenTimePoints from 'customer-data-filters-ui/filterQueryFormat/operator/BetweenTimePoints';
import NotBetweenTimePoints from 'customer-data-filters-ui/filterQueryFormat/operator/NotBetweenTimePoints';
import getConditionId from '../../utils/getConditionId';
import TimezoneDropdown from './TimezoneDropdown';
import { FlexDateInputWithTimezoneWrapper } from '../../constants/inlineLayout';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FilterOperatorDateRangeInputMoment = props => {
  const {
      condition,
      error,
      hideExtraInputs,
      inputUse,
      onBlur,
      onChange,
      getIsUngated,
      getTimezoneOptions,
      useOnePanel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [isOpen, setIsOpen] = useState(false);
  const {
    hasTimezones,
    field: fieldRecord
  } = condition;
  const isDateTimeOperator = fieldRecord && fieldRecord.type === DATE_TIME;
  const [showTimezoneToggle, setShowTimezoneToggle] = useState(false);
  useEffect(() => {
    if (hasRemoveTimezoneForDates(getIsUngated) && !isDateTimeOperator && !hasTimezones && (condition.constructor === BetweenTimePoints || condition.constructor === NotBetweenTimePoints)) {
      setShowTimezoneToggle(false);
    } else {
      setShowTimezoneToggle(!hideExtraInputs);
    }
  }, [condition.constructor, getIsUngated, hasTimezones, isDateTimeOperator, hideExtraInputs]);
  const getIsUTC = () => getIsUTCFromUtils(condition, getIsUngated);
  const handleUpdateClick = () => {
    if (!condition) {
      return;
    }
    setShowTimezoneToggle(false);
    const {
      value: startDate,
      highValue: endDate
    } = condition;
    if (I18n.moment.isMoment(startDate) && I18n.moment.isMoment(endDate)) {
      const timezoneConverter = I18n.moment.portalTz;
      onChange(syntheticEvent(condition.merge({
        value: timezoneConverter(startDate.format('YYYY-MM-DD')),
        highValue: timezoneConverter(endDate.format('YYYY-MM-DD'))
      })), {
        hasClickedUpdate: true
      });
    }
  };
  const onTimezoneChange = newUseUTC => {
    if (!condition) {
      return;
    }
    const {
      value: startDate,
      highValue: endDate
    } = condition;
    if (I18n.moment.isMoment(startDate) && I18n.moment.isMoment(endDate)) {
      const timezoneConverter = newUseUTC ? I18n.moment.utc : I18n.moment.portalTz;
      onChange(syntheticEvent(condition.merge({
        value: timezoneConverter(startDate.format('YYYY-MM-DD')),
        highValue: timezoneConverter(endDate.format('YYYY-MM-DD'))
      })));
    }
  };
  const onChangeLocal = evt => {
    if (hasRemoveTimezoneForDates(getIsUngated) && !hasTimezones) {
      const startDate = fromSimpleDateToMoment(evt.target.value.startDate, false);
      const endDate = fromSimpleDateToMoment(evt.target.value.endDate, false);
      onChange(syntheticEvent(condition.merge({
        value: startDate,
        highValue: endDate
      })));
    } else {
      const startDate = fromSimpleDateToMoment(evt.target.value.startDate, getIsUTC());
      const endDate = fromSimpleDateToMoment(evt.target.value.endDate, getIsUTC());
      onChange(syntheticEvent(condition.merge({
        value: startDate,
        highValue: endDate
      })));
    }
  };
  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (typeof onBlur === 'function' && isOpen === false && prevIsOpen.current === true) {
      onBlur();
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, onBlur]);
  const isError = error.get('error');
  const errorMessage = error.get('message');

  // @ts-expect-error we added types to `fromMomentToSimpleDate` after this was written
  const startDate = fromMomentToSimpleDate(condition.get('value'));
  // @ts-expect-error we added types to `fromMomentToSimpleDate` after this was written
  const endDate = fromMomentToSimpleDate(condition.get('highValue'));
  const inputValue = {
    startDate,
    endDate
  };
  const transferableProps = getTransferableProps(rest);
  const {
    ['data-selenium-test']: dataSeleniumTest
  } = transferableProps;

  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const disabled = transferableProps.disabled;
  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const readOnly = transferableProps.readOnly;
  const InputWrapper = useOnePanel ? FlexDateInputWithTimezoneWrapper : 'div';
  const TimezoneSelector = useOnePanel ? TimezoneDropdown : TimezoneToggle;
  return /*#__PURE__*/_jsx(FormControl, {
    "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
    error: isError,
    validationMessage: isError ? errorMessage : null,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(InputWrapper, {
        children: [/*#__PURE__*/_jsx(DateRange, {
          "data-test-id": "fr-date-range",
          "data-selenium-test": dataSeleniumTest,
          disabled: disabled,
          readOnly: readOnly,
          use: inputUse,
          onChange: onChangeLocal,
          onOpenChange: evt => setIsOpen(evt.target.value),
          value: inputValue
        }), showTimezoneToggle && /*#__PURE__*/_jsx(TimezoneSelector, {
          conditionId: getConditionId(condition),
          timezoneOptions: getTimezoneOptions(condition.field),
          fieldType: condition.field ? condition.field.get('type') : undefined,
          useUTC: getIsUTC(),
          onChange: onTimezoneChange,
          getIsUngated: getIsUngated
        })]
      }), showTimezoneToggle && hasRemoveTimezoneForDates(getIsUngated) && !isDateTimeOperator && /*#__PURE__*/_jsx(UpdateDateFilterWithTimezonesNotification, {
        onUpdateClick: handleUpdateClick,
        useUTC: getIsUTC()
      })]
    })
  });
};
export default FilterOperatorDateRangeInputMoment;